import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import { useClickOutside } from '../../../ModernScripts/Spotlight/Hooks/useClickOutside';
import { ColorPicker, ColorPickerProps } from '../color-picker/color-picker';

export interface SwatchProps {
    color: string;
    width?: number;
    height?: number;
}

export interface ColorPickerPopoverProps extends ColorPickerProps {
    isOpen?: boolean;
    width?: number;
    height?: number;
    dataId?: string;
}

const Swatch = styled.div<SwatchProps>`
    display: inline-block;
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    border: 1px solid #ededed;
    border-radius: 3px;
    background-color: ${(props) => props.color};
    cursor: pointer;
`;

const StyledColorPicker = styled(ColorPicker)`
    position: absolute;
    z-index: 1000;
`;

const StyledColorPickerPopover = styled.div``;

export const ColorPickerPopover = React.forwardRef<HTMLDivElement, ColorPickerPopoverProps>(
    (
        {
            color,
            currentPalette,
            palettes,
            isOpen = false,
            width = 50,
            height = 25,
            dataId = 'color-picker-popover',
            onColorChange,
            ...props
        },
        ref,
    ) => {
        const colorPickerRef = React.useRef<any | undefined>();
        const swatchRef = React.useRef<any | undefined>();
        const [showColorPicker, setShowColorPicker] = useState(isOpen);
        const [currentColor, setCurrentColor] = useState(color);
        const [currentTab, setCurrentTab] = useState(0);

        const toggleColorPicker = useCallback(
            () => setShowColorPicker(!showColorPicker),
            [showColorPicker, setShowColorPicker],
        );

        const handleColorChange = (color: any) => {
            const hex = color.hex ? color.hex : color;

            setCurrentColor(hex);
            onColorChange(hex);
        };

        useEffect(() => {
            function handleKeyListener(e: any) {
                const canCloseOnEsc = showColorPicker && e.key === 'Escape';
                canCloseOnEsc && setShowColorPicker(false);

                const isSwatchInFocus = document.activeElement === swatchRef.current;
                const canToggleOnSpace = isSwatchInFocus && e.key === ' ';
                canToggleOnSpace && toggleColorPicker();
            }

            document.addEventListener('keydown', handleKeyListener);

            return function cleanupKeyListener() {
                document.removeEventListener('keydown', handleKeyListener);
            };
        }, [showColorPicker, setShowColorPicker, toggleColorPicker]);

        useEffect(() => {
            setCurrentColor(color);
        }, [color]);

        useClickOutside(colorPickerRef, toggleColorPicker);

        return (
            <StyledColorPickerPopover {...props} ref={ref}>
                <Swatch
                    color={currentColor}
                    width={width}
                    height={height}
                    tabIndex={0}
                    ref={swatchRef}
                    onClick={toggleColorPicker}
                    data-test-id={`${dataId}__swatch-div`}
                />
                {showColorPicker && (
                    <StyledColorPicker
                        color={currentColor}
                        currentPalette={currentPalette}
                        palettes={palettes}
                        ref={colorPickerRef}
                        dataId={`${dataId}__color-picker`}
                        selectedTab={currentTab}
                        onTabChange={setCurrentTab}
                        onColorChange={handleColorChange}
                    />
                )}
            </StyledColorPickerPopover>
        );
    },
);

ColorPickerPopover.displayName = 'ColorPickerPopover';
